import { useEffect, useRef, useCallback } from 'react'

import { parseCookies } from 'nookies'
import { NotificationListItem } from 'services/notifications/types'

export interface NotificationChannelData extends NotificationListItem {
  icon: string
  navigation_url: string | null
  event_type: string
}

interface UseNotificationChannelProps {
  merchantUserUuid?: string
  onNotificationReceived?: (notification: NotificationChannelData) => void
  enabled?: boolean
}

const getWebSocketURL = (): string => {
  return 'wss://back.clientbase.com.br/cable'
}

export const NOTIFICATION_CHANNEL_NAME = 'NotificationChannel'

export const useNotificationChannel = ({
  merchantUserUuid,
  onNotificationReceived,
  enabled = true
}: UseNotificationChannelProps) => {
  const channelRef = useRef<ActionCable.Channel | null>(null)
  const consumerRef = useRef<ActionCable.Cable | null>(null)
  const isConnectedRef = useRef(false)
  const isConnectingRef = useRef(false)
  const hasAttemptedConnectionRef = useRef(false)
  const onNotificationReceivedRef = useRef(onNotificationReceived)
  const notifiedNotificationsRef = useRef<Set<string>>(new Set())

  // Keep callback ref updated without triggering reconnection
  useEffect(() => {
    onNotificationReceivedRef.current = onNotificationReceived
  }, [onNotificationReceived])

  // Single effect to manage connection lifecycle
  useEffect(() => {
    if (typeof window === 'undefined') return
    if (!enabled) return
    if (!merchantUserUuid) return

    // Prevent multiple connection attempts
    if (
      isConnectedRef.current ||
      isConnectingRef.current ||
      hasAttemptedConnectionRef.current
    ) {
      return
    }

    const { ['nextauth.token']: token } = parseCookies()
    if (!token) return

    const setupConnection = async () => {
      isConnectingRef.current = true
      hasAttemptedConnectionRef.current = true

      try {
        const actionCable = await import('actioncable')
        const wsURL = `${getWebSocketURL()}?token=${token}`

        consumerRef.current = actionCable.default.createConsumer(wsURL)

        channelRef.current = consumerRef.current.subscriptions.create(
          {
            channel: NOTIFICATION_CHANNEL_NAME,
            merchant_user_uuid: merchantUserUuid
          },
          {
            received: (rawData: NotificationChannelData | string) => {
              // ActionCable worker may send double-encoded JSON
              let notification: NotificationChannelData
              if (typeof rawData === 'string') {
                try {
                  notification = JSON.parse(rawData)
                } catch {
                  console.error('Failed to parse notification data:', rawData)
                  return
                }
              } else {
                notification = rawData
              }

              const notificationKey = notification.uuid
              if (!notificationKey) return

              // Prevent duplicate notifications
              if (notifiedNotificationsRef.current.has(notificationKey)) {
                return
              }

              notifiedNotificationsRef.current.add(notificationKey)

              // Call the callback if provided
              onNotificationReceivedRef.current?.(notification)
            },
            connected: () => {
              isConnectedRef.current = true
              isConnectingRef.current = false
            },
            disconnected: () => {
              isConnectedRef.current = false
              isConnectingRef.current = false
            },
            rejected: () => {
              isConnectingRef.current = false
            }
          }
        )
      } catch (error) {
        console.error('Error connecting to NotificationChannel:', error)
        isConnectingRef.current = false
      }
    }

    setupConnection()

    // Cleanup on unmount
    return () => {
      if (channelRef.current) {
        channelRef.current.unsubscribe()
        channelRef.current = null
      }
      if (consumerRef.current) {
        consumerRef.current.disconnect()
        consumerRef.current = null
      }
      isConnectedRef.current = false
      isConnectingRef.current = false
      hasAttemptedConnectionRef.current = false
    }
  }, [enabled, merchantUserUuid])

  // Clear notified notifications periodically (every 5 minutes)
  useEffect(() => {
    const interval = setInterval(() => {
      notifiedNotificationsRef.current.clear()
    }, 5 * 60 * 1000)

    return () => clearInterval(interval)
  }, [])

  const reconnect = useCallback(() => {
    if (channelRef.current) {
      channelRef.current.unsubscribe()
      channelRef.current = null
    }
    if (consumerRef.current) {
      consumerRef.current.disconnect()
      consumerRef.current = null
    }
    isConnectedRef.current = false
    isConnectingRef.current = false
    hasAttemptedConnectionRef.current = false
  }, [])

  return {
    isConnected: isConnectedRef.current,
    reconnect
  }
}

export default useNotificationChannel
